import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Button,
  Container,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  Snackbar,
  IconButton,
  Tooltip,
  Chip,
  Autocomplete,
} from '@mui/material';
import {
  ArrowBack,
  PersonAdd,
  Delete,
  Edit,
  Refresh,
  Restore,
  BadgeOutlined,
  Block,
  ContentCopy,
} from '@mui/icons-material';
import { btApi, BTTeacher, ChurchManager } from '../../services/api/btApi';
import { churchApi, Church } from '../../services/api/churchApi';
import {
  BT_TEACHER_AWANA_ROLE_OPTIONS,
  BT_TEACHER_CLUB_OPTIONS,
  BT_TEACHER_POSITION_TYPE_OPTIONS,
  normalizeBTTeacherForm,
} from '../../services/api/teacherOptions';

const emptyTeacherForm = {
  teacherName: '',
  teacherNameEn: '',
  teacherPhone: '',
  teacherEmail: '',
  churchManagerId: '',
  churchName: '',
  churchRegistrationNumber: '',
  positionType: '',
  awanaRole: '',
  club: '',
  experience: '',
  specialNotes: '',
};

const formatPhoneNumber = (value: string) => {
  const numbers = value.replace(/\D/g, '').slice(0, 11);
  if (numbers.length <= 3) return numbers;
  if (numbers.length <= 7) return `${numbers.slice(0, 3)}-${numbers.slice(3)}`;
  return `${numbers.slice(0, 3)}-${numbers.slice(3, 7)}-${numbers.slice(7)}`;
};

const isValidPhoneNumber = (value: string) => /^010-\d{4}-\d{4}$/.test(value);

const BTTeacherManagePage: React.FC = () => {
  const { churchManagerId } = useParams<{ churchManagerId: string }>();
  const navigate = useNavigate();
  
  const [churchManager, setChurchManager] = useState<ChurchManager | null>(null);
  const [churches, setChurches] = useState<Church[]>([]);
  const [teachers, setTeachers] = useState<BTTeacher[]>([]);
  const [loading, setLoading] = useState(false);
  const [teacherDialog, setTeacherDialog] = useState(false);
  const [teacherDetailDialog, setTeacherDetailDialog] = useState(false);
  const [selectedTeacher, setSelectedTeacher] = useState<BTTeacher | null>(null);
  const [editingTeacher, setEditingTeacher] = useState<BTTeacher | null>(null);
  const [teacherForm, setTeacherForm] = useState(emptyTeacherForm);
  const [selectedChurchId, setSelectedChurchId] = useState('');
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success' as 'success' | 'error',
  });

  useEffect(() => {
    if (churchManagerId) {
      fetchChurchManager();
    } else {
      fetchChurches();
    }
    fetchTeachers();
  }, [churchManagerId]);

  const fetchChurches = async () => {
    try {
      const response = await churchApi.searchChurches({ getAllResults: true });
      if (!response.success) {
        throw new Error(response.error);
      }
      setChurches(
        response.data.sort((first, second) => first.name.localeCompare(second.name, 'ko'))
      );
    } catch (error: any) {
      setSnackbar({
        open: true,
        message: '교회 목록을 불러오는데 실패했습니다.',
        severity: 'error',
      });
    }
  };

  const fetchChurchManager = async () => {
    try {
      const manager = await btApi.getChurchManagerById(churchManagerId!);
      setChurchManager(manager);
    } catch (error: any) {
      setSnackbar({
        open: true,
        message: '교회 정보를 불러오는데 실패했습니다.',
        severity: 'error',
      });
    }
  };

  const fetchTeachers = async () => {
    setLoading(true);
    try {
      const response = churchManagerId
        ? await btApi.getTeachersByChurchManager(churchManagerId)
        : await btApi.getBTTeachers({ limit: '1000' });
      setTeachers(response.data);
    } catch (error: any) {
      setSnackbar({
        open: true,
        message: '교사 목록을 불러오는데 실패했습니다.',
        severity: 'error',
      });
    } finally {
      setLoading(false);
    }
  };

  const openAddDialog = () => {
    setEditingTeacher(null);
    setSelectedChurchId('');
    setTeacherForm({
      ...emptyTeacherForm,
      churchManagerId: churchManagerId || '',
    });
    setTeacherDialog(true);
  };

  const openEditDialog = (teacher: BTTeacher) => {
    setTeacherDetailDialog(false);
    setSelectedTeacher(null);
    setEditingTeacher(teacher);
    setSelectedChurchId(
      churches.find((church) => church.name === teacher.churchName)?._id || ''
    );
    setTeacherForm({
      teacherName: teacher.teacherName,
      teacherNameEn: teacher.teacherNameEn || '',
      teacherPhone: teacher.teacherPhone,
      teacherEmail: teacher.teacherEmail || '',
      churchManagerId: teacher.churchManagerId && typeof teacher.churchManagerId === 'object'
        ? teacher.churchManagerId._id
        : teacher.churchManagerId || churchManagerId || '',
      churchName: teacher.churchName || (
        teacher.churchManagerId && typeof teacher.churchManagerId === 'object'
          ? teacher.churchManagerId.churchName
          : ''
      ),
      churchRegistrationNumber:
        churches.find((church) => church.name === teacher.churchName)?.mainId
        || churchManager?.metadata?.mainId
        || '',
      positionType: teacher.positionType || '',
      awanaRole: teacher.awanaRole || '',
      club: teacher.club || '',
      experience: teacher.experience || '',
      specialNotes: teacher.specialNotes || '',
    });
    setTeacherDialog(true);
  };

  const openDetailDialog = (teacher: BTTeacher) => {
    setSelectedTeacher(teacher);
    setTeacherDetailDialog(true);
  };

  const handleSaveTeacher = async () => {
    try {
      if (!/^[A-Z][A-Z .'-]*$/i.test(teacherForm.teacherNameEn.trim())) throw new Error('수료증에 사용할 영문 이름을 입력해주세요.');
      const selectedChurch = selectedChurchId
        ? churches.find((church) => church._id === selectedChurchId)
        : churches.find((church) => church.name === teacherForm.churchName);

      const teacherData = normalizeBTTeacherForm({
        ...teacherForm,
        churchManagerId: teacherForm.churchManagerId || churchManagerId || null,
        churchRegistrationNumber:
          teacherForm.churchRegistrationNumber
          || selectedChurch?.mainId
          || churchManager?.metadata?.mainId
          || '',
        clubName: teacherForm.club || '',
      });

      if (editingTeacher) {
        await btApi.updateBTTeacher(editingTeacher._id, teacherData);
      } else {
        await btApi.createBTTeacher(teacherData);
      }

      setSnackbar({
        open: true,
        message: editingTeacher ? '교사 정보가 수정되었습니다.' : '교사가 성공적으로 등록되었습니다.',
        severity: 'success',
      });
      setTeacherDialog(false);
      setEditingTeacher(null);
      setSelectedChurchId('');
      setTeacherForm(emptyTeacherForm);
      fetchTeachers();
    } catch (error: any) {
      setSnackbar({
        open: true,
        message: error.message || '교사 정보 저장에 실패했습니다.',
        severity: 'error',
      });
    }
  };

  const handleDeleteTeacher = async (teacherId: string) => {
    if (!window.confirm('정말로 이 교사를 삭제하시겠습니까?')) {
      return;
    }

    try {
      const response = await fetch(`/api/bt/teachers/${teacherId}`, {
        method: 'DELETE',
      });

      const data = await response.json();
      
      if (data.success) {
        setSnackbar({
          open: true,
          message: '교사가 삭제되었습니다.',
          severity: 'success',
        });

        setTeacherDetailDialog(false);
        setSelectedTeacher(null);
        fetchTeachers();
      } else {
        throw new Error(data.message || '교사 삭제에 실패했습니다.');
      }
    } catch (error: any) {
      setSnackbar({
        open: true,
        message: error.message,
        severity: 'error',
      });
    }
  };

  const handleRestoreTeacher = async (teacherId: string) => {
    if (!window.confirm('이 교사를 다시 사용 가능 상태로 복구하시겠습니까?')) {
      return;
    }

    try {
      const response = await fetch(`/api/bt/teachers/${teacherId}/status`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ status: 'registered' }),
      });

      const data = await response.json();

      if (!data.success) {
        throw new Error(data.message || data.error || '교사 복구에 실패했습니다.');
      }

      setSnackbar({
        open: true,
        message: '교사가 다시 사용 가능 상태로 복구되었습니다.',
        severity: 'success',
      });
      setTeacherDetailDialog(false);
      setSelectedTeacher(null);
      fetchTeachers();
    } catch (error: any) {
      setSnackbar({
        open: true,
        message: error.message || '교사 복구에 실패했습니다.',
        severity: 'error',
      });
    }
  };

  const handleTeacherIdStatus = async (teacher: BTTeacher) => {
    const enabled = !teacher.idCardEnabled;
    if (!enabled && !window.confirm(`${teacher.teacherName} 교사의 모바일 증명 사용을 중지하시겠습니까?`)) {
      return;
    }

    try {
      await btApi.updateTeacherIdCardStatus(teacher._id, enabled);
      setSnackbar({
        open: true,
        message: enabled ? '모바일 증명을 발급했습니다.' : '모바일 증명 사용을 중지했습니다.',
        severity: 'success',
      });
      setTeacherDetailDialog(false);
      setSelectedTeacher(null);
      fetchTeachers();
    } catch (error: any) {
      setSnackbar({ open: true, message: error.message || '증명 상태 변경에 실패했습니다.', severity: 'error' });
    }
  };

  const copyTeacherIdUrl = async () => {
    try {
      await navigator.clipboard.writeText(`${window.location.origin}/bt/teacher-id`);
      setSnackbar({ open: true, message: '교사용 증명 주소를 복사했습니다.', severity: 'success' });
    } catch {
      setSnackbar({ open: true, message: '주소를 복사하지 못했습니다.', severity: 'error' });
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'registered': return 'success';
      case 'cancelled': return 'error';
      default: return 'default';
    }
  };

  const getStatusText = (status: string) => {
    switch (status) {
      case 'registered': return '등록됨';
      case 'cancelled': return '취소됨';
      default: return status;
    }
  };

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 4 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <IconButton onClick={() => navigate(churchManagerId ? '/admin/bt-manage' : '/admin')}>
            <ArrowBack />
          </IconButton>
          <Box>
            <Typography variant="h4" component="h1">
              교사 관리
            </Typography>
            {churchManager && (
              <Typography variant="subtitle1" color="text.secondary">
                {churchManager.churchName} - {churchManager.managerName}
              </Typography>
            )}
          </Box>
        </Box>
        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', justifyContent: 'flex-end' }}>
          <Button
            variant="outlined"
            startIcon={<ContentCopy />}
            onClick={copyTeacherIdUrl}
          >
            교사용 주소
          </Button>
          <Button
            variant="outlined"
            startIcon={<Refresh />}
            onClick={fetchTeachers}
          >
            새로고침
          </Button>
          <Button
            variant="contained"
            startIcon={<PersonAdd />}
            onClick={openAddDialog}
          >
            교사 추가
          </Button>
        </Box>
      </Box>

      {/* 교사 목록 테이블 */}
      <Paper>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>이름</TableCell>
                {!churchManagerId && <TableCell>소속 교회</TableCell>}
                <TableCell>연락처</TableCell>
                <TableCell>직분</TableCell>
                <TableCell>등록일</TableCell>
                <TableCell align="center">작업</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {teachers.map((teacher) => (
                <TableRow
                  key={teacher._id}
                  hover
                  onClick={() => openDetailDialog(teacher)}
                  sx={{ cursor: 'pointer' }}
                >
                  <TableCell>{teacher.teacherName}</TableCell>
                  {!churchManagerId && (
                    <TableCell>
                      {teacher.churchManagerId && typeof teacher.churchManagerId === 'object'
                        ? teacher.churchManagerId.churchName
                        : teacher.churchName || '-'}
                    </TableCell>
                  )}
                  <TableCell>{teacher.teacherPhone}</TableCell>
                  <TableCell>{teacher.positionType || '-'}</TableCell>
                  <TableCell>
                    {new Date(teacher.registrationDate || teacher.createdAt).toLocaleDateString('ko-KR')}
                  </TableCell>
                  <TableCell align="center" onClick={(event) => event.stopPropagation()}>
                    {teacher.status === 'registered' && (
                      <Tooltip title="삭제">
                        <IconButton
                          size="small"
                          color="error"
                          onClick={() => handleDeleteTeacher(teacher._id)}
                          aria-label={`${teacher.teacherName} 삭제`}
                        >
                          <Delete fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    )}
                    {teacher.status === 'cancelled' && (
                      <Tooltip title="다시 사용">
                        <IconButton
                          size="small"
                          color="success"
                          onClick={() => handleRestoreTeacher(teacher._id)}
                          aria-label={`${teacher.teacherName} 복구`}
                        >
                          <Restore fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    )}
                  </TableCell>
                </TableRow>
              ))}
              {teachers.length === 0 && (
                <TableRow>
                  <TableCell colSpan={churchManagerId ? 5 : 6} align="center">
                    {loading ? '교사 목록을 불러오는 중입니다.' : '등록된 교사가 없습니다.'}
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      <Dialog
        open={teacherDetailDialog}
        onClose={() => {
          setTeacherDetailDialog(false);
          setSelectedTeacher(null);
        }}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>교사 상세 정보</DialogTitle>
        <DialogContent>
          {selectedTeacher && (
            <Box sx={{ pt: 1.5, display: 'flex', flexDirection: 'column', gap: 2 }}>
              <Box>
                <Typography variant="caption" color="text.secondary">교사번호</Typography>
                <Typography>{selectedTeacher.keyCode || '-'}</Typography>
              </Box>
              <Box>
                <Typography variant="caption" color="text.secondary">이메일</Typography>
                <Typography>{selectedTeacher.teacherEmail || '-'}</Typography>
              </Box>
              <Box>
                <Typography variant="caption" color="text.secondary">직분 / 역할</Typography>
                <Typography>
                  {[
                    selectedTeacher.positionType,
                    selectedTeacher.awanaRole,
                  ].filter(Boolean).join(' / ') || '-'}
                </Typography>
              </Box>
              {selectedTeacher.club && (
                <Box>
                  <Typography variant="caption" color="text.secondary">클럽</Typography>
                  <Typography>{selectedTeacher.club}</Typography>
                </Box>
              )}
              <Box>
                <Typography variant="caption" color="text.secondary">상태</Typography>
                <Box sx={{ mt: 0.5 }}>
                  <Chip
                    label={getStatusText(selectedTeacher.status)}
                    color={getStatusColor(selectedTeacher.status)}
                    size="small"
                  />
                </Box>
              </Box>
              <Box>
                <Typography variant="caption" color="text.secondary">모바일 증명</Typography>
                <Box sx={{ mt: 0.5 }}>
                  <Chip
                    label={selectedTeacher.idCardEnabled ? '사용 중' : '미발급'}
                    color={selectedTeacher.idCardEnabled ? 'success' : 'default'}
                    size="small"
                    variant={selectedTeacher.idCardEnabled ? 'filled' : 'outlined'}
                  />
                </Box>
              </Box>
              <Box>
                <Typography variant="caption" color="text.secondary">수강 강의</Typography>
                {selectedTeacher.attendedLectures && selectedTeacher.attendedLectures.length > 0 ? (
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75, mt: 0.75 }}>
                    {selectedTeacher.attendedLectures.map((lecture, index) => (
                      <Chip
                        key={`${selectedTeacher._id}-detail-lecture-${index}`}
                        label={lecture.title}
                        size="small"
                        variant="outlined"
                      />
                    ))}
                  </Box>
                ) : (
                  <Typography sx={{ mt: 0.5 }} color="text.secondary">없음</Typography>
                )}
              </Box>
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5, justifyContent: 'space-between' }}>
          <Button
            onClick={() => {
              setTeacherDetailDialog(false);
              setSelectedTeacher(null);
            }}
          >
            닫기
          </Button>
          {selectedTeacher && (
            <Box sx={{ display: 'flex', gap: 1 }}>
              <Tooltip title={selectedTeacher.idCardEnabled ? '증명 사용 중지' : '모바일 증명 발급'}>
                <span>
                  <IconButton
                    size="small"
                    color={selectedTeacher.idCardEnabled ? 'error' : 'success'}
                    onClick={() => handleTeacherIdStatus(selectedTeacher)}
                    aria-label={selectedTeacher.idCardEnabled ? `${selectedTeacher.teacherName} 증명 사용 중지` : `${selectedTeacher.teacherName} 증명 발급`}
                  >
                    {selectedTeacher.idCardEnabled ? <Block /> : <BadgeOutlined />}
                  </IconButton>
                </span>
              </Tooltip>
              <Tooltip title="수정">
                <IconButton size="small" color="primary" onClick={() => openEditDialog(selectedTeacher)}>
                  <Edit />
                </IconButton>
              </Tooltip>
              {selectedTeacher.status === 'registered' && (
                <Tooltip title="삭제">
                  <IconButton
                    size="small"
                    color="error"
                    onClick={() => handleDeleteTeacher(selectedTeacher._id)}
                  >
                    <Delete />
                  </IconButton>
                </Tooltip>
              )}
              {selectedTeacher.status === 'cancelled' && (
                <Tooltip title="다시 사용">
                  <IconButton
                    size="small"
                    color="success"
                    onClick={() => handleRestoreTeacher(selectedTeacher._id)}
                  >
                    <Restore />
                  </IconButton>
                </Tooltip>
              )}
            </Box>
          )}
        </DialogActions>
      </Dialog>

      {/* 교사 추가/수정 다이얼로그 */}
      <Dialog open={teacherDialog} onClose={() => setTeacherDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{editingTeacher ? '교사 정보 수정' : '교사 추가'}</DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
            {!churchManagerId && (
              <Autocomplete
                options={churches}
                value={
                  churches.find((church) => church._id === selectedChurchId) ||
                  churches.find((church) => church.name === teacherForm.churchName) ||
                  null
                }
                getOptionLabel={(church) => `${church.name} (${church.mainId}-${church.subId})`}
                isOptionEqualToValue={(option, value) => option._id === value._id}
                onChange={(_, church) => {
                  setSelectedChurchId(church?._id || '');
                  setTeacherForm({
                    ...teacherForm,
                    churchManagerId: '',
                    churchName: church?.name || '',
                    churchRegistrationNumber: church?.mainId || '',
                  });
                }}
                noOptionsText="검색 결과가 없습니다."
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="소속 교회 검색"
                    placeholder="교회명 또는 교회 코드 입력"
                    required
                  />
                )}
              />
            )}

            <TextField
              fullWidth
              label="이름"
              value={teacherForm.teacherName}
              onChange={(e) => setTeacherForm({...teacherForm, teacherName: e.target.value})}
              required
            />
            
            <TextField
              fullWidth
              label="영문 이름"
              value={teacherForm.teacherNameEn}
              onChange={(e) => setTeacherForm({...teacherForm, teacherNameEn: e.target.value.toUpperCase()})}
              placeholder="HONG GILDONG"
              required
            />

            <TextField
              fullWidth
              label="전화번호"
              value={teacherForm.teacherPhone}
              onChange={(e) => setTeacherForm({
                ...teacherForm,
                teacherPhone: formatPhoneNumber(e.target.value),
              })}
              placeholder="010-0000-0000"
              helperText={
                teacherForm.teacherPhone && !isValidPhoneNumber(teacherForm.teacherPhone)
                  ? '010으로 시작하는 휴대전화 번호 11자리를 입력하세요.'
                  : '숫자만 입력하면 하이픈이 자동으로 추가됩니다.'
              }
              error={Boolean(
                teacherForm.teacherPhone && !isValidPhoneNumber(teacherForm.teacherPhone)
              )}
              inputProps={{ inputMode: 'numeric', maxLength: 13 }}
              required
            />
            
            <TextField
              fullWidth
              label="이메일"
              type="email"
              value={teacherForm.teacherEmail}
              onChange={(e) => setTeacherForm({...teacherForm, teacherEmail: e.target.value})}
            />
            
            <FormControl fullWidth>
              <InputLabel>직분</InputLabel>
              <Select
                value={teacherForm.positionType}
                label="직분"
                onChange={(e) => setTeacherForm({...teacherForm, positionType: e.target.value})}
              >
                {BT_TEACHER_POSITION_TYPE_OPTIONS.map((option) => (
                  <MenuItem key={option} value={option}>{option}</MenuItem>
                ))}
              </Select>
            </FormControl>

            <FormControl fullWidth>
              <InputLabel>어와나 역할</InputLabel>
              <Select
                value={teacherForm.awanaRole}
                label="어와나 역할"
                onChange={(e) => setTeacherForm({...teacherForm, awanaRole: e.target.value})}
              >
                {BT_TEACHER_AWANA_ROLE_OPTIONS.map((option) => (
                  <MenuItem key={option} value={option}>{option}</MenuItem>
                ))}
              </Select>
            </FormControl>

            <FormControl fullWidth>
              <InputLabel>클럽</InputLabel>
              <Select
                value={teacherForm.club}
                label="클럽"
                onChange={(e) => setTeacherForm({...teacherForm, club: e.target.value})}
              >
                {BT_TEACHER_CLUB_OPTIONS.map((option) => (
                  <MenuItem key={option} value={option}>{option}</MenuItem>
                ))}
              </Select>
            </FormControl>
            
            <TextField
              fullWidth
              label="교육 경력"
              multiline
              rows={2}
              value={teacherForm.experience}
              onChange={(e) => setTeacherForm({...teacherForm, experience: e.target.value})}
            />
            
            <TextField
              fullWidth
              label="특이사항"
              multiline
              rows={2}
              value={teacherForm.specialNotes}
              onChange={(e) => setTeacherForm({...teacherForm, specialNotes: e.target.value})}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setTeacherDialog(false)}>취소</Button>
          <Button
            variant="contained"
            onClick={handleSaveTeacher}
            disabled={
              !teacherForm.teacherName ||
              !isValidPhoneNumber(teacherForm.teacherPhone) ||
              (!churchManagerId && !teacherForm.churchName)
            }
          >
            {editingTeacher ? '저장' : '추가'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* 스낵바 */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({...snackbar, open: false})}
      >
        <Alert severity={snackbar.severity} onClose={() => setSnackbar({...snackbar, open: false})}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Container>
  );
};

export default BTTeacherManagePage;
